import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { Loader2, Plus, Trash2, X } from "lucide-react"
import { useModalOpen } from "../lib/useModalOpen"

type ActivityTypesModalProps = {
  types: string[]
  onClose: () => void
  onSave: (types: string[]) => Promise<void>
}

export function ActivityTypesModal({ types, onClose, onSave }: ActivityTypesModalProps) {
  useModalOpen(true)
  const [draft, setDraft] = useState<string[]>(() => (types.length > 0 ? [...types] : [""]))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(types.length > 0 ? [...types] : [""])
  }, [types])

  const normalized = useMemo(
    () =>
      Array.from(
        new Set(
          draft
            .map((entry) => entry.trim())
            .filter(Boolean)
        )
      ),
    [draft]
  )

  async function handleSave() {
    setSaving(true)
    try {
      await onSave(normalized)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/28 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-xl max-h-[86vh] flex flex-col rounded-2xl border border-white/[0.08] bg-black/42 backdrop-blur-xl shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] overflow-hidden">
        <div className="shrink-0 px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">0x05 · Activity Config</p>
            <h3 className="text-[11px] font-mono font-bold text-white/90 uppercase tracking-widest">Activity Types</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          <p className="font-mono text-[11px] text-white/45">
            These options power the Activity Type selectors in activity creation and template editing.
          </p>

          {draft.map((value, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={value}
                onChange={(event) =>
                  setDraft((prev) => {
                    const next = [...prev]
                    next[index] = event.target.value
                    return next
                  })
                }
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/25"
                placeholder="Activity type name"
              />
              <button
                type="button"
                onClick={() => {
                  setDraft((prev) => {
                    const next = prev.filter((_, i) => i !== index)
                    return next.length > 0 ? next : [""]
                  })
                }}
                className="p-2 rounded-lg border border-white/[0.08] bg-white/[0.03] text-white/35 hover:text-red-300 hover:border-red-400/30 hover:bg-red-500/10 transition-colors"
                aria-label="Remove activity type"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => setDraft((prev) => [...prev, ""])}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-white/[0.12] bg-white/[0.02] text-white/45 font-mono text-[10px] font-bold uppercase tracking-wider hover:border-teal-400/30 hover:text-teal-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add type
          </button>
        </div>

        <div className="shrink-0 px-4 py-3 border-t border-white/[0.06] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="py-2 px-3 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/55 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/15 text-teal-300 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/25 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Save activity types
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
